import React from 'react';
import { BgImage } from '../components/Img';
import { IMG } from '../images';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink } from 'lucide-react';

const Disclaimer = () => {
  return (
    <div>
      {/* Hero Section */}
      <section 
        className="hero" 
        style={{
          minHeight: '40vh'
        }}
      >
        <BgImage image={IMG.facade} position="50% 40%" priority />
        <div className="hero-content">
          <h1>Website Disclaimer</h1>
          <p>Important information about using this website</p>
        </div>
      </section>

      {/* Content */}
      <section className="section">
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            
            <div style={{ background: 'var(--light-gray)', padding: '2rem', border: '1px solid var(--border-color)', borderLeft: '4px solid var(--safety-orange)', marginBottom: '3rem' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <AlertTriangle size={32} style={{ color: 'var(--safety-orange)', flexShrink: 0 }} />
                <div>
                  <h3 style={{ color: 'var(--navy-blue)', marginBottom: '0.5rem' }}>Please Read Carefully</h3>
                  <p style={{ lineHeight: 1.7 }}>
                    The information provided on this website is for general informational purposes only. Please review this disclaimer before using our services.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem', fontSize: '2rem' }}>General Information</h2>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                The information provided on this website is for general informational purposes only.
              </p>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                While ProClimb Solutions aims to keep the information accurate and up to date, we make no guarantees regarding the completeness, accuracy or reliability of any content.
              </p>
            </div>

            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem', fontSize: '2rem' }}>No Professional Advice</h2>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                Content on this website should not be considered professional engineering, structural or safety advice.
              </p>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                All rope access works must be assessed individually through site inspections and risk assessments conducted by qualified professionals.
              </p>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                Every project is unique and requires proper evaluation of site conditions, access requirements, and safety considerations.
              </p>
            </div>

            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem', fontSize: '2rem' }}>External Links</h2>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                Our website may include links to third-party websites. ProClimb Solutions is not responsible for the content or privacy practices of those sites.
              </p>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                We recommend reviewing the terms and privacy policies of any third-party sites you visit through links on our website.
              </p>
              <div style={{ background: 'var(--light-gray)', padding: '1.5rem', border: '1px solid var(--border-color)', marginTop: '1.5rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <ExternalLink size={20} style={{ color: 'var(--safety-orange)', flexShrink: 0, marginTop: '2px' }} />
                <p style={{ fontSize: '0.95rem', lineHeight: 1.7 }}>
                  External links are provided for convenience only and do not constitute an endorsement of the linked sites or their content.
                </p>
              </div>
            </div>

            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem', fontSize: '2rem' }}>Limitation of Liability</h2>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                To the maximum extent permitted by law, ProClimb Solutions shall not be liable for any loss or damage arising from reliance on information contained on this website.
              </p>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                This includes, but is not limited to:
              </p>
              <ul style={{ marginLeft: '2rem', marginBottom: '1rem', lineHeight: 1.8 }}>
                <li>Direct or indirect financial loss</li>
                <li>Loss of data or business opportunity</li>
                <li>Damages arising from use or inability to use this website</li>
                <li>Errors or omissions in website content</li>
                <li>Interruptions or delays in website access</li>
              </ul>
            </div>

            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem', fontSize: '2rem' }}>Changes to Website Content</h2>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                ProClimb Solutions reserves the right to modify, update or remove content from this website at any time without prior notice.
              </p>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                Services, pricing, and availability may change. Always confirm details directly with ProClimb Solutions before making decisions based on website information.
              </p>
            </div>

            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem', fontSize: '2rem' }}>Images and Photography</h2>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                Images shown on this website are for illustrative purposes. Actual project results may vary based on site conditions, requirements, and specifications.
              </p>
              <p style={{ lineHeight: 1.8 }}>
                Some images may be stock photography and may not represent ProClimb Solutions' actual projects or work sites.
              </p>
            </div>

            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem', fontSize: '2rem' }}>Professional Services</h2>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                All rope access services provided by ProClimb Solutions are subject to our <Link to="/terms-conditions" style={{ color: 'var(--safety-orange)', textDecoration: 'none', fontWeight: 600 }}>Terms & Conditions</Link>.
              </p>
              <p style={{ marginBottom: '1rem', lineHeight: 1.8 }}>
                Quotes provided are estimates based on information available at the time. Final pricing may vary following site inspection and detailed assessment.
              </p>
              <p style={{ lineHeight: 1.8 }}>
                For specific project advice, safety assessments, or detailed quotes, please <Link to="/contact" style={{ color: 'var(--safety-orange)', textDecoration: 'none', fontWeight: 600 }}>contact us directly</Link>.
              </p>
            </div>

            <div style={{ marginBottom: '3rem' }}>
              <h2 style={{ color: 'var(--navy-blue)', marginBottom: '1.5rem', fontSize: '2rem' }}>Australian Consumer Law</h2>
              <p style={{ lineHeight: 1.8 }}>
                Nothing in this disclaimer is intended to exclude, restrict or modify any rights you may have under the Australian Consumer Law or other applicable laws that cannot be excluded, restricted or modified by agreement.
              </p>
            </div>

            <div style={{ background: 'var(--light-gray)', padding: '2rem', border: '1px solid var(--border-color)', marginTop: '3rem' }}>
              <p style={{ marginBottom: '1rem' }}>Last Updated: January 2025</p>
              <p>For specific questions or professional advice regarding rope access services, please contact ProClimb Solutions directly:</p>
              <p style={{ marginTop: '1rem' }}>
                <strong>Email:</strong> <a href="mailto:info@proclimbsolutions.com.au" style={{ color: 'var(--safety-orange)' }}>info@proclimbsolutions.com.au</a><br />
                <strong>Phone:</strong> <a href="tel:0426920301" style={{ color: 'var(--safety-orange)' }}>0426 920 301</a>
              </p>
            </div>

            <div style={{ textAlign: 'center', marginTop: '3rem' }}>
              <Link to="/contact" className="btn-primary">Contact Us for Professional Advice</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Disclaimer;
